import { Injectable } from "@angular/core";
import * as chroma from "chroma-js";
import * as wkt from 'wkt';
import * as geojson from "geojson";
import * as bbox from "geojson-bbox";
import { LocationSnapshotFilters } from "../types/LocationSnapshotFilters";
import { MapLegendsData } from "../types/MapLegendsData";

@Injectable()
export class SuspectHeatMapService {

    bounds: any;
    infoWindow: google.maps.InfoWindow;
    mapLegendsData:MapLegendsData=null;

    async plotHeatMapTiles(map: google.maps.Map, heatMapData: Array<any>, legendDivID: string, isAp: boolean, googleMaps: any, isCalculateLegendBreakPoint: boolean) {

        let mva_mtd_values = [];
        if (isAp) {
            mva_mtd_values = heatMapData.map(item => item.mtd);
        } else {
            mva_mtd_values = heatMapData.map(item => item.mtDnoAP);
        }
        if (isCalculateLegendBreakPoint) {
            //For the MTD values, generate breaks and colors
            this.mapLegendsData = this.getBreaksAndColors(mva_mtd_values);
        }

        //Set the Legend
        let mapLegend = document.getElementById(legendDivID) as HTMLElement;
        mapLegend.innerHTML = '';
        const div = document.createElement("div");
        div.style.cssText = 'text-align: center;';
        div.innerHTML = '<b>MTD</b><hr/>';
        mapLegend.appendChild(div);

        if (mva_mtd_values.length > 0) {
            this.mapLegendsData.colors.forEach((color, i) => {
                const div = document.createElement("div");
                div.innerHTML = `<svg height="20" width="50">  <rect width="40" height="20" style="stroke-width:3;fill:${color}" /> </svg>` + `${this.mapLegendsData.breaks_legend[i]}`;
                mapLegend.appendChild(div);
            });
        } else { //i.e if there are no values..

            //Set legend text accordingly.
            const div = document.createElement("div");
            div.innerHTML = "<p>No data to display.</p>";
            mapLegend.appendChild(div);

        }

        //determine the color code based on its MTD value. This function will be used down the line.
        const getColor = (mva_mtd: number): string => {

            if (mva_mtd || mva_mtd == 0) {

                if (mva_mtd == 0) return this.mapLegendsData.colors[0];
                return this.mapLegendsData.colors[this.mapLegendsData.breaks.findIndex(n => n >= mva_mtd)];

            } else {
                return "#FFFFFF";
            }

        }

        let geoData: Array<any> = [];
        if (isAp) {

            heatMapData.forEach(item => {
                var mtdData = wkt.parse(item.geogReduce);
                if (mtdData.type == "GeometryCollection") {
                    mtdData.geometries = mtdData.geometries.filter(geo => geo.type != "Point");
                }

                geoData.push({
                    type: "alt_heatmap_tile",
                    mtd: item.mtd,
                    fillColor: getColor(item.mtd),
                    geo: mtdData
                });

            });

        } else {
            heatMapData.forEach(item => {
                var mtDnoAPData = wkt.parse(item.geogReduce);
                if (mtDnoAPData.type == "GeometryCollection") {
                    mtDnoAPData.geometries = mtDnoAPData.geometries.filter(geo => geo.type != "Point");
                }
                if (mtDnoAPData.type == 'GeometryCollection') {
                    geoData.push({
                        type: "alt_heatmap_tile",
                        mtd: item.mtDnoAP,
                        fillColor: getColor(item.mtDnoAP),
                        geo: mtDnoAPData
                    });
                }
            });
        }

        //Parse the geo-data array into GeoJSON and create a Feature Collection object.
        // References -
        // 1. https://www.npmjs.com/package/geojson
        // 2. https://geojson.org/geojson-spec.html#examples
        let featureCollection = geojson.parse(geoData, { GeoJSON: 'geo' });

        //Generate a bounding box for this feature collection. will not be visible in the map.
        //Use this bounding box to determine the center point and set it as the map view's center.      


        //Add the geo-json to the map. This will generate all the polygons in the map (i.e counties)
        map.data.addGeoJson(featureCollection);

        const handleTileClick = (event) => {

            let feature = event.feature;

            if (feature.getProperty("type") != 'alt_heatmap_tile') return;

            let mtd = feature.getProperty("mtd");

            if (!mtd && mtd != 0) return;

            let content = `<span style="font-size:small">${mtd}</span>`;

            if (this.infoWindow) {
                this.infoWindow.close();
            }
            this.infoWindow = new googleMaps.maps.InfoWindow();
            this.infoWindow.setContent(content);
            this.infoWindow.setPosition(event.latLng);
            this.infoWindow.open(map);
        }

        //Listener for click event, to display info when user clicks on a county.
        map.data.addListener('click', handleTileClick);

    }

    async plotCompetitorSites(map: google.maps.Map, competitorPlantsData: Array<any>, googleMap: any) {

        const { AdvancedMarkerElement } = await googleMap.maps.importLibrary("marker");

        const parser = new DOMParser();

        let compSVGString = '<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10">  <circle cx="5" cy="5" r="4" stroke="black" stroke-width="0.5" fill="black" /> </svg>';


        competitorPlantsData.forEach(compPlant => {

            let point = wkt.parse(compPlant.geoLocation);

            let marker = new AdvancedMarkerElement({
                map: map,
                position: { lat: point.coordinates[1], lng: point.coordinates[0] },
                content: parser.parseFromString(compSVGString, 'image/svg+xml').documentElement,
                title: compPlant.plantCoSite
            });

        });

    }

    getBreaksAndColors(values: number[]): MapLegendsData {
        //the input 'values' is an array of numbers using which the map will be color coded; Ex MTD values.
        //For the given 'values', we will generate the breaks and colors array.

        // Ex: If MTD Values = [0.39,0.76,1.52,1.62,2.11,2.48,16.22,19.71]
        // Then, breaks = [1,4,7,11,14,17,20]
        // And, breaks_legend = ["0-1","1-4","4-7","7-11","11-14","14-17","17-20"]
        // And, colors = ["#008000","#80c000","#ffff00","#ffd200","#ffa500","#ff5200","#ff0000"]

        //sort input and remove dupes
        let valuesUniq = Array.from(new Set(values.sort((a, b) => a - b)));

        //n is the number of breaks to be generated; maximum is 5
        let n = (valuesUniq.length >= 5) ? 5 : valuesUniq.length;

        let breaks = [];
        let colors = [];
        let breaks_legend = [];

        if (n != 0) {
            //generate 'n' equidistant breaks and round them up. Refer to https://gka.github.io/chroma.js/#chroma-limits
            breaks = Array.from(new Set(chroma.limits(valuesUniq, 'e', n - 1)
                .sort((a, b) => a - b)
                .map(mtd => Math.ceil(mtd))));

            //In some cases where MTD value is 0
            if (breaks[0] == 0) {
                //If 0 is the only value we replace it with 1, so that legend shows as 0-1. otherwise it will show as 0-0 (wrong).
                if (breaks.length == 1) {
                    breaks[0] = 1;
                } else { //there are other values, just remove zero in the first position and legends will be generated fine.
                    breaks = breaks.slice(1);
                }

            }

            let colorBands = [];
            if (breaks.length <= 2) {
                colorBands = ['green', 'yellow'];
            } else if (breaks.length <= 4) {
                colorBands = ['green', 'yellow', 'orange'];
            } else {
                colorBands = ['green', 'yellow', 'orange', 'red'];
            }
            //generate equidistant colors. Refer to https://gka.github.io/chroma.js/#color-scales
            colors = chroma.scale(colorBands).colors(breaks.length);

            //generate the breaks legend labels
            breaks.forEach((val, i) => {
                if (i == 0 && breaks[0] != 0) { breaks_legend.push(`0 - ${val}`) }
                else { breaks_legend.push(`${breaks[i - 1]} - ${breaks[i]}`) }
            });
        }

        return { breaks: breaks, breaks_legend: breaks_legend, colors: colors };
    }

    setFeatureStyles(map: google.maps.Map) {

        let heatMapStyle = {
            strokeColor: "#808080",
            strokeOpacity: 1.0,
            strokeWeight: 2,
            fillOpacity: 0.7,
            zIndex: 1
        }

        let bullsEyeStyle = {
            strokeColor: "#4d4d4d",
            strokeOpacity: 1.0,
            strokeWeight: 2,
            fillOpacity: 0.0,
            zIndex: 10
        };

        let miles125Style = {
            strokeColor: "#4d4d4d",
            strokeOpacity: 1.0,
            strokeWeight: 2,
            fillOpacity: 0.0,
            zIndex: 10
        };

        map.data.setStyle(function (feature) {

            let type = feature.getProperty('type');

            if (type == 'alt_heatmap_tile') {
                heatMapStyle['fillColor'] = feature.getProperty("fillColor");
                return heatMapStyle;
            }

            if (type == 'bullsEye') return bullsEyeStyle;

            if (type == 'miles125') return miles125Style;

        });

    }

    async plotProposedPlantSites(map: google.maps.Map, proposedPlantsData: Array<any>, googleMaps: any) {

        const { AdvancedMarkerElement } = await googleMaps.maps.importLibrary("marker");

        const parser = new DOMParser();

        let proposedPlantSVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                                        <rect width="10" height="10" x="10" y="-4" transform="rotate(45)" 
                                          style="fill:lime;stroke-width:1;stroke:rgb(0,0,0)" />
                                      </svg>`;

        proposedPlantsData.forEach(proposedPlant => {

            let point = wkt.parse(proposedPlant.geoLocation);

            let marker = new AdvancedMarkerElement({
                map: map,
                position: { lat: point.coordinates[1], lng: point.coordinates[0] },
                content: parser.parseFromString(proposedPlantSVGString, 'image/svg+xml').documentElement,
                title: proposedPlant.plantCoSite
            });

        });

    }

    async plotCompetitorAPSites(map: google.maps.Map, competitorAPPlantsData: Array<any>, googleMaps: any) {

        const { AdvancedMarkerElement } = await googleMaps.maps.importLibrary("marker");

        const parser = new DOMParser();

        let compAP_SVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15">
                                  <rect width="9" height="9" x="3" y="3" 
                                    style="fill:lime;stroke-width:1;stroke:rgb(0,0,0)" />
                                </svg>`;

        competitorAPPlantsData.forEach(compAPPlant => {

            let point = wkt.parse(compAPPlant.geoLocation);

            let marker = new AdvancedMarkerElement({
                map: map,
                position: { lat: point.coordinates[1], lng: point.coordinates[0] },
                content: parser.parseFromString(compAP_SVGString, 'image/svg+xml').documentElement,
                title: compAPPlant.plantCoSite
            });

        });

    }

    async plotAPPlantSites(map: google.maps.Map, apPlantsData: Array<any>, googleMaps: any, filters: LocationSnapshotFilters) {

        const { AdvancedMarkerElement } = await googleMaps.maps.importLibrary("marker");

        const parser = new DOMParser();

        let color = (filters.plantCompany == 'Air Products') ? 'lime' : 'red';

        let apPlantSVGString = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15">
                                  <rect width="15" height="15" style="fill:${color};stroke-width:2;stroke:rgb(0,0,0)" />
                                </svg>`;


        let miles125Array = [];

        apPlantsData.forEach(apPlant => {

            let point = wkt.parse(apPlant.geoLocation);

            let marker = new AdvancedMarkerElement({
                map: map,
                position: { lat: point.coordinates[1], lng: point.coordinates[0] },
                content: parser.parseFromString(apPlantSVGString, 'image/svg+xml').documentElement,
                title: apPlant.plantCoSite
            });

            miles125Array.push({ type: 'miles125', geo: wkt.parse(apPlant.miles125) });

        });

        // Looks like creating a extra ring on Maps
        // let featureCollection = geojson.parse(miles125Array, { GeoJSON: 'geo' });

        // map.data.addGeoJson(featureCollection);

    }

    async plotBullsEye(map: google.maps.Map, bullsEyeData: Array<any>, googleMaps: any) {

        //Add rings
        let ringArray = [];
        bullsEyeData.forEach(item => {
            ringArray.push({ type: 'bullsEye', geo: wkt.parse(item.bullseye) });
        });

        let featureCollection = geojson.parse(ringArray, { GeoJSON: 'geo' });

        if (!this.bounds) {
            let boundingBox = bbox(featureCollection);
            this.bounds = new google.maps.LatLngBounds();
            this.bounds.extend({ lat: boundingBox[1], lng: boundingBox[0] });
            this.bounds.extend({ lat: boundingBox[3], lng: boundingBox[2] });
        }


        map.data.addGeoJson(featureCollection);

        //Add distance labels to the rings
        const { AdvancedMarkerElement } = await googleMaps.maps.importLibrary("marker");
        const parser = new DOMParser();

        bullsEyeData.forEach(item => {

            let point = wkt.parse(item.geoPoint);

            let svgText = `<svg xmlns="http://www.w3.org/2000/svg" height="30" width="60">
                            <text x="5" y="28" font-size="12" 
                              font-weight="bold" fill="black">${item.dist} miles</text>
                          </svg>`;

            let marker = new AdvancedMarkerElement({
                map: map,
                position: { lat: point.coordinates[1], lng: point.coordinates[0] },
                content: parser.parseFromString(svgText, 'image/svg+xml').documentElement,
            });

        });

    }

    adjustSuspectsMapViewport(altHeatMap: google.maps.Map) {
        altHeatMap.fitBounds(this.bounds);
        altHeatMap.setCenter(this.bounds.getCenter());
    }
}